import React, { useState, useEffect } from 'react';
import './LoginPage.css';
import { apiCall } from '../config/api';

const ResetPassword = ({ token, onBackToLogin, onResetSuccess }) => {
  const [formData, setFormData] = useState({
    password: '',
    confirmPassword: ''
  });
  const [isLoading, setIsLoading] = useState(false);
  const [isValidating, setIsValidating] = useState(true);
  const [tokenValid, setTokenValid] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    validateToken();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const validateToken = async () => {
    if (!token) {
      setTokenValid(false);
      setIsValidating(false);
      return;
    }

    try {
      const response = await apiCall(`/auth/validate-reset-token/${token}`, {
        method: 'GET',
      });

      const data = await response.json();
      setTokenValid(data.valid === true);
    } catch (error) {
      console.error('Token validation error:', error);
      setTokenValid(false);
    } finally {
      setIsValidating(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.password) {
      setError('Password is required');
      return;
    }
    
    if (formData.password.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }
    
    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    setIsLoading(true);
    setError('');

    try {
      const response = await apiCall('/auth/reset-password', {
        method: 'POST',
        body: JSON.stringify({
          token,
          password: formData.password
        }),
      });

      const data = await response.json();

      if (response.ok) {
        setSuccess(true);
        if (onResetSuccess) {
          setTimeout(() => onResetSuccess(), 3000);
        }
      } else {
        setError(data.error || 'Failed to reset password. Please try again.');
      }
    } catch (error) {
      console.error('Reset password error:', error);
      setError('Network error. Please check your connection and try again.');
    } finally {
      setIsLoading(false);
    }
  };

  if (isValidating) {
    return (
      <div className="login-container">
        <div className="login-card">
          <div className="login-header">
            <img 
              src={`${process.env.PUBLIC_URL}/assets/images/Aminole-Logo.png`} 
              alt="Aminole Logo" 
              className="login-logo" 
            />
            <h2>Validating...</h2>
            <p>Please wait while we verify your reset link.</p>
          </div>
          <div className="loading-spinner"></div>
        </div>
      </div>
    );
  }

  if (!tokenValid) {
    return (
      <div className="login-container">
        <div className="login-card">
          <div className="login-header">
            <img 
              src={`${process.env.PUBLIC_URL}/assets/images/Aminole-Logo.png`} 
              alt="Aminole Logo" 
              className="login-logo" 
            />
            <h2>Invalid or Expired Link</h2>
            <p>This password reset link is no longer valid.</p>
          </div>

          <div className="error-box">
            <p>The link may have expired or already been used. Password reset links are valid for 1 hour.</p>
          </div>

          <div className="login-footer">
            <button 
              type="button" 
              className="link-button"
              onClick={onBackToLogin}
            >
              ← Back to Login
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div className="login-container">
        <div className="login-card">
          <div className="login-header">
            <img 
              src={`${process.env.PUBLIC_URL}/assets/images/Aminole-Logo.png`} 
              alt="Aminole Logo" 
              className="login-logo" 
            />
            <h2>Password Reset!</h2>
            <p>Your password has been successfully changed.</p>
          </div>

          <div className="success-message">
            <div className="success-icon">✅</div>
            <p>You can now log in with your new password.</p>
            <p className="success-note">Redirecting to login...</p>
          </div>

          <div className="login-footer">
            <button 
              type="button" 
              className="login-button"
              onClick={onBackToLogin}
            >
              Go to Login
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="login-container">
      <div className="login-card">
        <div className="login-header">
          <img 
            src={`${process.env.PUBLIC_URL}/assets/images/Aminole-Logo.png`} 
            alt="Aminole Logo" 
            className="login-logo" 
          />
          <h2>Reset Password</h2>
          <p>Enter your new password below.</p>
        </div>

        <form onSubmit={handleSubmit} className="login-form">
          {error && (
            <div className="error-message general-error">
              {error}
            </div>
          )}

          <div className="form-group">
            <label htmlFor="password">New Password</label>
            <input
              type="password"
              id="password"
              name="password"
              value={formData.password}
              onChange={handleInputChange}
              className={error ? 'error' : ''}
              placeholder="Enter new password (min 6 characters)"
              autoFocus
            />
          </div>

          <div className="form-group">
            <label htmlFor="confirmPassword">Confirm Password</label>
            <input
              type="password"
              id="confirmPassword"
              name="confirmPassword"
              value={formData.confirmPassword}
              onChange={handleInputChange}
              className={error ? 'error' : ''}
              placeholder="Confirm your new password"
            />
          </div>

          <button 
            type="submit" 
            className="login-button"
            disabled={isLoading}
          >
            {isLoading ? 'Resetting...' : 'Reset Password'}
          </button>
        </form>

        <div className="login-footer">
          <button 
            type="button" 
            className="link-button"
            onClick={onBackToLogin}
          >
            ← Back to Login
          </button>
        </div>
      </div>
    </div>
  );
};

export default ResetPassword;
